import { Heart, Building2, Handshake } from "lucide-react";
import { ServiceCard } from "./ServiceCard";
import { ServicesComparison } from "@/components/ServicesComparison";
import { useServicesContent } from "@/hooks/useServicesContent";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { Skeleton } from "@/components/ui/skeleton";

interface ServicesGridProps {
  onSchedule: (serviceType: string) => void;
}

const iconMap: Record<string, any> = {
  Heart,
  Building2,
  Handshake,
};

export function ServicesGrid({ onSchedule }: ServicesGridProps) {
  const { content: mainServices, loading: servicesLoading } = useServicesContent("main_services");
  const { getSetting, isLoading: settingsLoading } = useSiteSettings();
  
  const showComparisonTables = getSetting('show_comparison_tables', 'false') === 'true';

  const serviceComparisons = [
    {
      title: "Acompanhamento Nutricional",
      description: "Plano personalizado para reeducação alimentar e mudança de hábitos",
      services: [
        { name: "Consulta inicial completa (90 min)", included: true },
        { name: "Anamnese alimentar detalhada", included: true },
        { name: "Avaliação antropométrica", included: true },
        { name: "Plano alimentar personalizado", included: true },
        { name: "Lista de substituições", included: true },
        { name: "Retorno em 30 dias", included: true },
        { name: "Suporte via WhatsApp", included: true },
        { name: "Receitas exclusivas", included: false },
        { name: "Acompanhamento semanal", included: false }
      ]
    },
    {
      title: "Personal Diet",
      description: "Acompanhamento intensivo com suporte contínuo",
      services: [
        { name: "Tudo do Acompanhamento Nutricional", included: true },
        { name: "Consultas semanais", included: true },
        { name: "Suporte diário via WhatsApp", included: true },
        { name: "Cardápio semanal detalhado", included: true },
        { name: "Receitas exclusivas", included: true },
        { name: "Orientação para compras", included: true },
        { name: "Ajustes em tempo real", included: true },
        { name: "Monitoramento diário", included: true },
        { name: "Relatórios de progresso", included: true }
      ]
    },
    {
      title: "Nutrição Esportiva",
      description: "Especializado para atletas e praticantes de atividade física",
      services: [
        { name: "Avaliação da composição corporal", included: true },
        { name: "Plano alimentar para treino", included: true },
        { name: "Estratégia de hidratação", included: true },
        { name: "Suplementação orientada", included: true },
        { name: "Periodização nutricional", included: true },
        { name: "Acompanhamento de performance", included: true },
        { name: "Orientação pré e pós-treino", included: true },
        { name: "Plano para competições", included: false },
        { name: "Acompanhamento presencial", included: false }
      ]
    },
    {
      title: "Reeducação Alimentar",
      description: "Foco na mudança gradual e sustentável de hábitos",
      services: [
        { name: "Identificação de padrões alimentares", included: true },
        { name: "Educação nutricional", included: true },
        { name: "Estratégias comportamentais", included: true },
        { name: "Plano de transição gradual", included: true },
        { name: "Material educativo", included: true },
        { name: "Acompanhamento psicológico", included: false },
        { name: "Grupos de apoio", included: false },
        { name: "Oficinas culinárias", included: false },
        { name: "Suporte familiar", included: false }
      ]
    }
  ];

  const isLoading = servicesLoading || settingsLoading;

  return (
    <section className="py-16">
      <div className="container mx-auto px-6">
        {isLoading ? (
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-96 w-full" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {mainServices
              .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
              .map((service) => {
                const serviceData = service.content_value as any;
                const IconComponent = iconMap[serviceData.icon] || Heart;
                
                return (
                  <ServiceCard
                    key={service.id}
                    icon={IconComponent}
                    title={serviceData.title}
                    description={serviceData.description}
                    features={serviceData.features || []}
                    buttonText={serviceData.buttonText}
                    onSchedule={() => onSchedule(serviceData.serviceType)}
                  />
                );
              })}
          </div>
        )}

        {/* Services Comparison Tables - Conditional */}
        {showComparisonTables && (
          <div className="mt-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-primary mb-4 text-center">
              Compare Nossos Serviços
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 text-center mb-8 sm:mb-12 max-w-3xl mx-auto">
              Veja o que está incluído em cada tipo de atendimento e escolha o melhor para você
            </p>
            
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
              {serviceComparisons.map((comparison, index) => (
                <ServicesComparison
                  key={index}
                  title={comparison.title}
                  description={comparison.description}
                  services={comparison.services}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
